import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from "../api/api.js";
import { 
  Container, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Button, 
  Paper,
  Divider,
  Grid
} from '@mui/material';
import CustomerDetailsForm from '../components/CustomerDetailsForm.jsx';
const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  // Helper function to normalize profile data to camelCase
  const normalizeProfileData = (data) => {
    if (!data) return null;
    return {
      firstName: data.first_name || data.firstName || '',
      lastName: data.last_name || data.lastName || '',
      email: data.email || '',
      phone: data.phone || data.customer_phone || '',
      address: data.address || data.shipping_address || '',
      city: data.city || data.shipping_city || '',
      state: data.state || data.shipping_state || '',
      zipCode: data.zip_code || data.shipping_zip_code || data.zipCode || '',
      country: data.country || data.shipping_country || ''
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/api/users/profile');
        setProfile(normalizeProfileData(response.data));
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to fetch profile. Please try again later.');
        }
        console.error('Profile fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      navigate('/login');
      return;
    }

    fetchProfile();
  }, [navigate]);

  const handleDetailsSaved = (details) => {
    setProfile(prev => ({
      ...prev,
      ...normalizeProfileData(details)
    }));
    setEditing(false);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ my: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ my: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          My Profile
        </Typography>
        {!editing && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => setEditing(true)}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      
      {editing ? (
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Edit Your Details</Typography>
          <CustomerDetailsForm 
            initialData={profile}
            onComplete={handleDetailsSaved}
          />
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Personal Information</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
              <Typography variant="body1" gutterBottom>
                {profile.firstName} {profile.lastName}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Email</Typography>
              <Typography variant="body1" gutterBottom>
                {profile.email}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
              <Typography variant="body1" gutterBottom>
                {profile.phone || 'Not provided'}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">Address</Typography>
              <Typography variant="body1" gutterBottom>
                {profile.address || 'Not provided'}
              </Typography>
              {profile.city && profile.state && profile.zipCode && (
                <Typography variant="body1" gutterBottom>
                  {`${profile.city}, ${profile.state} ${profile.zipCode}`}
                </Typography>
              )}
              {profile.country && (
                <Typography variant="body1">
                  {profile.country}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default Profile;
